import { Node as ProseMirrorNode } from "prosemirror-model";
import { z } from "zod";

import { schema } from "../schema/index.js";

/** One mark as it appears in the public `data` JSON. */
export const markJsonSchema = z.object({
  type: z.string().min(1),
  attrs: z.record(z.string(), z.unknown()).optional(),
});

export type MarkJson = z.infer<typeof markJsonSchema>;

export interface NodeJson {
  type: string;
  attrs?: Record<string, unknown> | undefined;
  content?: NodeJson[] | undefined;
  marks?: MarkJson[] | undefined;
  text?: string | undefined;
}

/** One node of the public `data` JSON; recursive through `content`. */
export const nodeJsonSchema: z.ZodType<NodeJson> = z.lazy(() =>
  z.object({
    type: z.string().min(1),
    attrs: z.record(z.string(), z.unknown()).optional(),
    content: z.array(nodeJsonSchema).optional(),
    marks: z.array(markJsonSchema).optional(),
    text: z.string().optional(),
  }),
);

/** The document body as the API exposes it. */
export const documentJsonSchema = nodeJsonSchema.refine(
  (value) => value.type === "doc",
  "the document body must be a `doc` node",
);

export class InvalidDocumentJsonError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidDocumentJsonError";
  }
}

/**
 * Round-trips the JSON through the schema and through JSON.parse/stringify so
 * that attribute defaults and key order are stable. Two documents that differ
 * only in those respects canonicalize to the same value, which is what keeps
 * a no-op collaborative save from registering as an edit.
 */
export function canonicalizeDoc(json: unknown): NodeJson {
  const parsed = documentJsonSchema.safeParse(json);
  if (!parsed.success) {
    throw new InvalidDocumentJsonError(parsed.error.issues[0]?.message ?? "invalid document JSON");
  }
  let node: ProseMirrorNode;
  try {
    node = ProseMirrorNode.fromJSON(schema, parsed.data);
  } catch (cause) {
    throw new InvalidDocumentJsonError(
      cause instanceof Error ? cause.message : "invalid document JSON",
    );
  }
  return JSON.parse(JSON.stringify(node.toJSON())) as NodeJson;
}

/** True when two document bodies are identical once canonicalized. */
export function sameDoc(left: unknown, right: unknown): boolean {
  return JSON.stringify(canonicalizeDoc(left)) === JSON.stringify(canonicalizeDoc(right));
}
